import { formatShortDate } from './dates';
import { seriesRule } from './history';
import { type RecurrenceRule, type SkippedDate, isSkipped, skippedOf } from './recurrence';
import type { ScheduleEvent } from './types';

/**
 * Excluir **só uma data** de uma série que se repete — o culto que cai num feriado, o
 * domingo do evento especial. A série continua; a data fica guardada na regra
 * (`RecurrenceRule.skipped`, a mesma em todas as datas, como o resto da regra), com o
 * motivo, e por isso não volta ao estender o término (`seriesEndPlan`) nem nas ocorrências
 * passadas (`backfillPlan`). Quem estava na escala publicada que ia acontecer é avisado
 * ("… foi cancelado") e a data pode ser restaurada com a configuração da série.
 *
 * O evento sem regra (único ou "sem data fixa") não tem padrão para trazer a data de
 * volta: a exclusão dele é a simples (`deleteEvents`).
 */

/** Atalhos do motivo, no diálogo de exclusão (o texto é livre). */
export const SKIP_REASONS = ['Feriado', 'Evento especial', 'Mudança de agenda'] as const;
export const MAX_SKIP_REASON = 80;

/** As datas excluídas da série, da mais antiga à mais recente. */
export function seriesSkipped(events: readonly ScheduleEvent[], seriesId: string): SkippedDate[] {
  return [...skippedOf(seriesRule(events, seriesId))].sort((a, b) => a.date.localeCompare(b.date));
}

/** Exclui só esta data guardando-a na regra? Só na série que se repete e tem outras datas. */
export function skipsDate(events: readonly ScheduleEvent[], event: ScheduleEvent): boolean {
  return (
    !!seriesRule(events, event.seriesId) &&
    events.some((e) => e.seriesId === event.seriesId && e.id !== event.id)
  );
}

/** Quem é avisado do cancelamento: os escalados da escala publicada de hoje em diante. */
export function cancellationAudience(event: ScheduleEvent, today: string): string[] {
  if (!event.published || event.date < today) return [];

  return [...new Set(Object.values(event.slots).flat())];
}

export function skipReasonError(reason: string): string | null {
  return reason.trim().length > MAX_SKIP_REASON
    ? `O motivo tem no máximo ${MAX_SKIP_REASON} caracteres.`
    : null;
}

const byDateTime = (a: ScheduleEvent, b: ScheduleEvent) => (a.date + a.time).localeCompare(b.date + b.time);

const withSkipped = (rule: RecurrenceRule, skipped: SkippedDate[]): RecurrenceRule => {
  if (skipped.length) return { ...rule, skipped };

  const { skipped: _gone, ...rest } = rule;

  return rest;
};

/**
 * Tira a data da série e a guarda na regra das outras, com o motivo e quem é avisado. Fora
 * de uma série que se repete (ou sendo a última data dela), só exclui.
 */
export function skipOccurrence(
  events: ScheduleEvent[],
  event: ScheduleEvent,
  options: { reason: string; today: string; at: number }
): ScheduleEvent[] {
  const rest = events.filter((e) => e.id !== event.id);

  if (!skipsDate(events, event)) return rest;

  const entry: SkippedDate = {
    date: event.date,
    time: event.time,
    reason: options.reason.trim().slice(0, MAX_SKIP_REASON),
    notified: cancellationAudience(event, options.today),
    at: options.at,
  };
  const rule = seriesRule(events, event.seriesId)!;
  const skipped = [...skippedOf(rule).filter((s) => s.date !== event.date), entry].sort((a, b) =>
    a.date.localeCompare(b.date)
  );
  const ruled = withSkipped(rule, skipped);

  return rest.map((e) => (e.seriesId === event.seriesId ? { ...e, rule: ruled } : e));
}

/** Por que a data excluída não pode voltar, ou `null`. */
export function restoreSkippedError(
  events: readonly ScheduleEvent[],
  seriesId: string,
  date: string
): string | null {
  if (!events.some((e) => e.seriesId === seriesId)) return 'O evento não existe mais.';
  if (!isSkipped(seriesRule(events, seriesId), date))
    return 'Esta data não está entre as excluídas da série.';
  if (events.some((e) => e.seriesId === seriesId && e.date === date))
    return 'A série já tem um evento nesta data.';

  return null;
}

/**
 * Traz a data excluída de volta: o evento nasce com a configuração da série (a última data
 * não alterada sozinha), no horário que tinha e sem escala — id `{série}-{data}`, com
 * sufixo se já usado —, e a data sai das excluídas.
 */
export function restoreSkipped(
  events: ScheduleEvent[],
  seriesId: string,
  date: string
): { events: ScheduleEvent[]; created: ScheduleEvent | null } {
  if (restoreSkippedError(events, seriesId, date)) return { events, created: null };

  const series = events.filter((e) => e.seriesId === seriesId).sort(byDateTime);
  const template = series.filter((e) => !e.edited).at(-1) ?? series.at(-1)!;
  const rule = seriesRule(events, seriesId)!;
  const entry = skippedOf(rule).find((s) => s.date === date)!;
  const ruled = withSkipped(
    rule,
    skippedOf(rule).filter((s) => s.date !== date)
  );
  const ids = new Set(events.map((e) => e.id));
  let id = `${seriesId}-${date}`;

  for (let n = 2; ids.has(id); n++) id = `${seriesId}-${date}-${n}`;

  const created: ScheduleEvent = {
    ...template,
    id,
    date,
    time: entry.time,
    rule: ruled,
    ministryIds: [...template.ministryIds],
    need: { ...template.need },
    slots: {},
    generated: false,
    published: false,
    edited: false,
    responses: {},
    attendance: {},
    nudgedAt: null,
    recorded: false,
    notices: {},
    swaps: [],
    audience: null,
  };

  return {
    events: [...events.map((e) => (e.seriesId === seriesId ? { ...e, rule: ruled } : e)), created].sort(
      byDateTime
    ),
    created,
  };
}

/** A data excluída sem quem foi avisado (a anonimização) — e o recorte de quem só vê o seu. */
export function keepNotified(event: ScheduleEvent, keep: (memberId: string) => boolean): ScheduleEvent {
  const skipped = skippedOf(event.rule);

  if (!event.rule || !skipped.some((s) => s.notified.some((id) => !keep(id)))) return event;

  return {
    ...event,
    rule: { ...event.rule, skipped: skipped.map((s) => ({ ...s, notified: s.notified.filter(keep) })) },
  };
}

/** "25 dez · Feriado" / "25 dez". */
export function skippedLabel(skipped: SkippedDate): string {
  return skipped.reason
    ? `${formatShortDate(skipped.date)} · ${skipped.reason}`
    : formatShortDate(skipped.date);
}
